/**
 * Admin design tokens (black-and-white Shopify-derived layout), shared by admin-dashboard and theme-builder.
 * Source of truth for tokens.css (generated: `pnpm --filter @iws/admin-ui-tokens build:css`) and the Tailwind preset.
 * Colours are hex here and emitted as HSL channels so `hsl(var(--x) / <alpha>)` tints work (`bg-success/16`).
 * Each palette family has lighter / light / DEFAULT / dark / darker. Values and their sources:
 * docs/ADMIN_SHOPIFY_REDESIGN_PLAN.md §2.
 */
export const palette = {
  // Neutral ramp: 100 table header / subdued surface, 200 row divider / neutral badge, 300 card border,
  // 500 faint text, 600 muted text, 700 secondary text, 800 ink, 900 frame.
  grey: { 50: '#fafafa', 100: '#f7f7f7', 200: '#f2f2f2', 300: '#e3e3e3', 400: '#cccccc', 500: '#a6a6a6', 600: '#707070', 700: '#4a4a4a', 800: '#101010', 900: '#0a0a0a' },
  // Black and white: primary actions are black; `lighter` is the selected-tab fill, `bright` the active nav marker on
  // the black sidebar.
  brand: { lighter: '#ebebeb', light: '#707070', DEFAULT: '#111111', dark: '#0a0a0a', darker: '#000000', bright: '#ffffff' },
  // Attention (yellow) tone.
  cta: { lighter: '#ffef9d', light: '#ffe066', DEFAULT: '#e5b800', dark: '#4f4700', darker: '#332e00' },
  info: { lighter: '#d5f6ff', light: '#a1cae7', DEFAULT: '#38afe2', dark: '#24586d', darker: '#123544' },
  success: { lighter: '#dcfce7', light: '#86efac', DEFAULT: '#16a34a', dark: '#166534', darker: '#14532d' },
  warning: { lighter: '#ffd6a4', light: '#ffb85c', DEFAULT: '#e8a227', dark: '#5e4200', darker: '#3d2a00' },
  error: { lighter: '#fde7e6', light: '#f19994', DEFAULT: '#c61d14', dark: '#731c15', darker: '#4e0f0b' },
  // Analytics blues (funnel, links).
  violet: { lighter: '#e5ebff', light: '#9fb2f4', DEFAULT: '#3b5de0', dark: '#1b47db', darker: '#10205e' },
  // App frame and sidebar surfaces: frame, hover, active pill.
  hero: { DEFAULT: '#0a0a0a', 2: '#181818', 3: '#2b2b2b' },
}

/** Semantic aliases: name -> palette path or hex. Keeps the shadcn names existing components use. */
export const semantic = {
  page: '#ffffff',
  card: '#ffffff',
  ink: 'grey.800',
  'text-2': 'grey.700',
  faint: 'grey.500',
  line: 'grey.300',
  background: '#ffffff',
  foreground: 'grey.800',
  'card-foreground': 'grey.800',
  popover: '#ffffff',
  'popover-foreground': 'grey.800',
  primary: 'brand.DEFAULT',
  'primary-hover': '#2a2a2a',
  'primary-foreground': '#ffffff',
  secondary: '#ebebeb',
  'secondary-foreground': 'grey.800',
  muted: 'grey.200',
  'muted-foreground': 'grey.600',
  accent: 'grey.100',
  'accent-foreground': 'grey.800',
  destructive: 'error.DEFAULT',
  'destructive-foreground': '#ffffff',
  'success-foreground': 'success.dark',
  'warning-foreground': 'warning.dark',
  border: 'grey.300',
  'border-subtle': 'grey.200',
  'border-strong': '#dddddd',
  input: '#d9d9d9',
  ring: '#537ef9',
  link: 'violet.dark',
  'placeholder-foreground': '#757575',
  // Fills: secondary buttons and chips, their hover, selected pill tab, subdued surfaces.
  fill: '#ebebeb',
  'fill-hover': '#e3e3e3',
  selected: '#dedede',
  subdued: 'grey.100',
  tooltip: '#1a1a1a',
  frame: 'hero.DEFAULT',
  sidebar: 'hero.DEFAULT',
  'sidebar-background': 'hero.DEFAULT',
  'sidebar-foreground': '#e3e3e3',
  'sidebar-muted': 'grey.500',
  'sidebar-hover': 'hero.2',
  'sidebar-input': '#515151',
  'sidebar-primary': '#ffffff',
  'sidebar-primary-foreground': '#ffffff',
  'sidebar-accent': 'hero.3',
  'sidebar-accent-foreground': '#ffffff',
  'sidebar-border': '#262626',
  'sidebar-ring': '#9bacf9',
  // Marker bar and icon of the active nav item.
  'sidebar-active': 'brand.bright',
  // Analytics series: current period, comparison period, then funnel blue, attention, neutral.
  'chart-1': '#4fa9ea',
  'chart-2': '#a1cae7',
  'chart-3': 'violet.DEFAULT',
  'chart-4': 'cta.DEFAULT',
  'chart-5': 'grey.500',
  'chart-axis': '#70707a',
  'chart-grid': '#eeeeef',
}

export const radius = { xs: '4px', sm: '6px', md: '8px', lg: '12px', xl: '16px', '2xl': '20px' }

export const shadow = {
  card: '0 0 0 1px rgb(0 0 0 / 0.03), 0 1px 2px rgb(0 0 0 / 0.04), 0 4px 12px rgb(0 0 0 / 0.03)',
  dropdown: '0 0 0 1px rgb(0 0 0 / 0.06), 0 8px 24px rgb(0 0 0 / 0.12)',
  dialog: '0 20px 60px rgb(0 0 0 / 0.25)',
  z1: '0 1px 2px rgb(0 0 0 / 0.06)',
  z8: '0 4px 12px rgb(0 0 0 / 0.08)',
  bar: '0 8px 24px rgb(0 0 0 / 0.2)',
  button: '0 1px 0 rgb(0 0 0 / 0.05)',
}

/**
 * Shopify type scale (Inter, measured cap heights): 28 display, 26 page title, 15 card/section title,
 * 14 body, 12 caption. The older Minimal names map onto it so existing call sites follow.
 */
export const typeScale = {
  display: ['1.75rem', { lineHeight: '2.125rem', fontWeight: '650' }],
  'heading-xl': ['1.625rem', { lineHeight: '2rem', fontWeight: '650' }],
  'heading-lg': ['1.25rem', { lineHeight: '1.5rem', fontWeight: '650' }],
  'heading-md': ['0.9375rem', { lineHeight: '1.25rem', fontWeight: '650' }],
  'body-md': ['0.875rem', { lineHeight: '1.25rem' }],
  micro: ['0.6875rem', { lineHeight: '0.875rem', fontWeight: '550' }],
  h1: ['1.75rem', { lineHeight: '2.125rem', fontWeight: '650' }],
  h2: ['1.625rem', { lineHeight: '2rem', fontWeight: '650' }],
  h3: ['1.25rem', { lineHeight: '1.5rem', fontWeight: '650' }],
  h4: ['1.0625rem', { lineHeight: '1.5rem', fontWeight: '650' }],
  h5: ['0.9375rem', { lineHeight: '1.25rem', fontWeight: '650' }],
  h6: ['0.9375rem', { lineHeight: '1.25rem', fontWeight: '650' }],
  subtitle1: ['0.875rem', { lineHeight: '1.25rem', fontWeight: '650' }],
  subtitle2: ['0.875rem', { lineHeight: '1.25rem', fontWeight: '550' }],
  body1: ['0.875rem', { lineHeight: '1.25rem' }],
  body2: ['0.875rem', { lineHeight: '1.25rem' }],
  caption: ['0.75rem', { lineHeight: '1rem' }],
  overline: ['0.6875rem', { lineHeight: '0.875rem', fontWeight: '550' }],
}

/** Tailwind's default size steps; sm = 14px body, so Bangla (Hind Siliguri) stays readable. */
export const baseFontSize = {
  xs: ['0.75rem', { lineHeight: '1rem' }],
  sm: ['0.875rem', { lineHeight: '1.25rem' }],
  base: ['0.875rem', { lineHeight: '1.25rem' }],
  lg: ['0.9375rem', { lineHeight: '1.25rem' }],
  xl: ['1.25rem', { lineHeight: '1.5rem' }],
  '2xl': ['1.625rem', { lineHeight: '2rem' }],
  '3xl': ['1.75rem', { lineHeight: '2.125rem' }],
}

/** Inter variable weights as Polaris uses them. */
export const fontWeight = { normal: '450', medium: '550', semibold: '650', bold: '700' }

export const fontFamily = {
  sans: ['Inter', '"Hind Siliguri"', '-apple-system', 'BlinkMacSystemFont', '"Segoe UI"', 'sans-serif'],
  heading: ['Inter', '"Hind Siliguri"', '-apple-system', 'BlinkMacSystemFont', '"Segoe UI"', 'sans-serif'],
  bn: ['"Hind Siliguri"', 'Inter', 'sans-serif'],
}

/** "#rrggbb" -> "h s% l%" channels. */
export function hexToHslChannels(hex) {
  const n = parseInt(hex.slice(1), 16)
  const r = ((n >> 16) & 255) / 255
  const g = ((n >> 8) & 255) / 255
  const b = (n & 255) / 255
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const l = (max + min) / 2
  let h = 0
  let s = 0
  if (max !== min) {
    const d = max - min
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    if (max === r) h = (g - b) / d + (g < b ? 6 : 0)
    else if (max === g) h = (b - r) / d + 2
    else h = (r - g) / d + 4
    h *= 60
  }
  const f = (x) => Math.round(x * 10) / 10
  return `${f(h)} ${f(s * 100)}% ${f(l * 100)}%`
}

/** CSS var name for a palette entry: brand.DEFAULT -> --brand, grey.500 -> --grey-500. */
export const varName = (family, shade) => (shade === 'DEFAULT' ? `--${family}` : `--${family}-${shade}`)

export function resolve(ref) {
  if (ref.startsWith('#')) return ref
  const [family, shade] = ref.split('.')
  return palette[family][shade]
}
